import { useEffect, useState, useCallback } from "react";
import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import VenueCard from "../../components/VenueCard";
import SearchIcon from "/public/images/icons/search.svg?react";
import TextInput from "../../components/TextInput";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function Venues() {
  const { setTitle, setSearchFunc, setBackButton } = useHeaderContext();
  const [searchOverlayVisible, setSearchOverlayVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredLocations, setFilteredLocations] = useState([]);
  const { user, logout, updateTokens } = useUserContext();
  const [venues, setVenues] = useState([]);

  const toggleSearchOverlay = () => {
    setSearchOverlayVisible(!searchOverlayVisible);
  };

  const searchVenues = useCallback((term) => {
    setSearchTerm(term);
    if (term) {
      setFilteredLocations(
        venues.filter((location) =>
          location.DirectoryDisplayName.toLowerCase().includes(term.toLowerCase())
        )
      );
    } else {
      setFilteredLocations(venues);
    }
  }, [venues]);

  useEffect(() => {
    setTitle("V E N U E S");
    setBackButton(true);
    setSearchFunc(() => toggleSearchOverlay(searchOverlayVisible));
  }, [searchOverlayVisible]);

  useEffect(() => {
    const getVenues = async () => {
      try {
        return await Sentry.startSpan({ name: "Fetch Venues", op: "http.request" }, async (span) => {
          const url = `${import.meta.env.VITE_API_URL}Venue/FetchVenueDirectory`;
          span.setAttribute("http.url", url);
  
          const response = await fetchWithRefresh(url, {
            headers: {
              "Content-Type": "/json",
              Authorization: `Bearer ${user.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });
  
          if (!response.ok) {
            const errorMessage = `Failed to fetch venues. Status: ${response.status}`;
            span.setStatus("error");
            Sentry.captureMessage(errorMessage, "warning");
            throw new Error(errorMessage);
          }
  
          const data = await response.json();
          setVenues(data);
          setFilteredLocations(data);
          span.setStatus("ok");
        });
      } catch (error) {
        Sentry.captureException(error, { extra: { userToken: user.token } });
        LogErrors(user, logout, updateTokens, "Error fetching venues: " + error);
      }
    };
  
    getVenues();
  }, [user.token]);

  // LogErrors(user, logout, updateTokens, 'filteredLocations', filteredLocations);

  return (
    <>
      <div className="flex flex-col">
        <p className="text-[#101820] text-[12px] tracking-[0.2em] font-semibold">
          GET ACTIVE
        </p>
        <h1 className="text-[#1E1E1E] text-[32px] leading-[42x] font-semibold mb-[32px]">
          Discover Sports Guardian Venues
        </h1>
      </div>
      <div className="flex flex-col gap-y-8">
        {filteredLocations.map((venue, index) => (
          <VenueCard
            key={index}
            venue={venue}
          />
        ))}
      </div>
      {searchOverlayVisible && (
        <div className="absolute top-0 left-0 flex justify-center items-start w-full">
          <div className="bg-white h-[160px] p-6 rounded w-full max-w-md">
            <div className="relative w-full mb-4">
              <TextInput
                name="search"
                type="text"
                label="Search Venues"
                className="w-full [&>.relative>input]:pr-[42px] [&>.label]:p-0 [&>.label]:pb-1"
                autoComplete="off"
                onChange={(e) => {
                  searchVenues(e.target.value);
                }}
                value={searchTerm}
              />
              <SearchIcon className="absolute w-6 h-6 stroke-black right-[14px] bottom-[14px]" />
            </div>
            {filteredLocations.length > 0 && (
              <div>
                <p className="text-[#101820] text-[24px] font-semibold">
                  Top Results
                </p>
              </div>
            )}

            <div className="flex flex-col gap-y-8 overflow-y-auto">
              {filteredLocations.length === 0 && (
                <>
                  <p className="text-[24px] leading-[34px] font-semibold text-[#101820]">
                    Sorry we couldn&apos;t find any matches for: {searchTerm}
                  </p>
                  <p className="font-semibold text-[#909090]">
                    Please try searching with another term
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}